import { useState, useEffect, useCallback, useMemo } from 'react';
import { Users, Plus, Search, Edit2, UserX, UserCheck } from 'lucide-react';
import apiService from '../services/api';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import Toast from '../components/Toast';
import { useToast } from '../hooks/useToast';
import { formatInstructeurName, getInstructeurLabels } from '../utils/instructeurLabels';
import { getOperationalCommercantId } from '../utils/operationalCommercantScope';

const JOURS = [
  { value: 1, label: 'Lundi' },
  { value: 2, label: 'Mardi' },
  { value: 3, label: 'Mercredi' },
  { value: 4, label: 'Jeudi' },
  { value: 5, label: 'Vendredi' },
  { value: 6, label: 'Samedi' },
  { value: 0, label: 'Dimanche' },
];

const initialFormData = {
  prenom: '',
  nom: '',
  email: '',
  telephone: '',
  specialites: '',
  bio: '',
  prestationIds: [],
};

const emptyAbsenceForm = {
  dateDebutLocal: '',
  dateFinLocal: '',
  motif: '',
  type: '',
};

function toDatetimeLocalValue(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromDatetimeLocalValue(local) {
  if (!local) return null;
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

function formatWarningToast(warning) {
  if (!warning?.count) return null;
  return warning.warningMessage || `${warning.count} rendez-vous sont à replanifier`;
}

const Praticiens = () => {
  const [instructeurs, setInstructeurs] = useState([]);
  const [commercant, setCommercant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatut, setFilterStatut] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [editingInstructeur, setEditingInstructeur] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [prestationsPersonnel, setPrestationsPersonnel] = useState([]);
  const [prestationSearch, setPrestationSearch] = useState('');
  const [staffWarning, setStaffWarning] = useState(null);

  const [activeTab, setActiveTab] = useState('infos');
  const [modeHoraires, setModeHoraires] = useState('COMMERCE');
  const [horaires, setHoraires] = useState([]);
  const [commerceHoraires, setCommerceHoraires] = useState([]);
  const [indisponibilites, setIndisponibilites] = useState([]);
  const [planningWarning, setPlanningWarning] = useState(null);
  const [planningLoading, setPlanningLoading] = useState(false);
  const [newPlageByDay, setNewPlageByDay] = useState({});
  const [absenceForm, setAbsenceForm] = useState(emptyAbsenceForm);
  const [editingAbsenceId, setEditingAbsenceId] = useState(null);

  const { toasts, toast, removeToast } = useToast();

  const commercantId = getOperationalCommercantId();

  const labels = useMemo(
    () => getInstructeurLabels(commercant?.categorie),
    [commercant?.categorie],
  );

  const fetchData = useCallback(async () => {
    if (!commercantId) {
      setError('Aucune boutique associée à votre compte.');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const [commercantData, instructeursData, prestationsData] = await Promise.all([
        apiService.getCommercant(commercantId),
        apiService.getInstructeurs(commercantId),
        apiService.getPrestationsPersonnelForInstructeurs(commercantId).catch(() => ({ prestations: [] })),
      ]);
      setCommercant(commercantData);
      const list = instructeursData.instructeurs || [];
      setInstructeurs(list);
      setPrestationsPersonnel(prestationsData.prestations || []);
      const actifs = list.filter((i) => i.actif).length;
      const prestaSansLien = (prestationsData.prestations || []).filter((p) => {
        const linked = list.some(
          (i) => i.actif && (i.prestationIds || []).includes(p.id),
        );
        return !linked;
      });
      if (actifs >= 2 && prestaSansLien.length > 0) {
        setStaffWarning(
          `${prestaSansLien.length} prestation(s) nécessitant du personnel n’ont aucun praticien affecté. La réservation en ligne sera bloquée tant que la configuration n’est pas complète.`,
        );
      } else {
        setStaffWarning(null);
      }
    } catch (err) {
      setError(err.message || 'Erreur lors du chargement des praticiens');
    } finally {
      setLoading(false);
    }
  }, [commercantId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const loadPlanning = useCallback(async (instructeurId) => {
    if (!instructeurId) return;
    setPlanningLoading(true);
    try {
      const [config, indispoData, anomalies] = await Promise.all([
        apiService.getInstructeurHorairesConfig(instructeurId),
        apiService.getInstructeurIndisponibilites(instructeurId, { actif: true }),
        apiService.getInstructeurPlanningAnomalies(instructeurId).catch(() => null),
      ]);
      setModeHoraires(config.modeHoraires || 'COMMERCE');
      setHoraires(config.horaires || []);
      setCommerceHoraires(config.commerceHoraires || []);
      setIndisponibilites(indispoData.indisponibilites || []);
      setPlanningWarning(anomalies?.count ? anomalies : null);
    } catch (err) {
      toast.error(err.message || 'Impossible de charger le planning');
    } finally {
      setPlanningLoading(false);
    }
  }, [toast]);

  const filteredInstructeurs = instructeurs.filter((item) => {
    if (filterStatut === 'ACTIF' && !item.actif) return false;
    if (filterStatut === 'INACTIF' && item.actif) return false;
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    const fullName = formatInstructeurName(item).toLowerCase();
    return (
      fullName.includes(term) ||
      item.email?.toLowerCase().includes(term) ||
      item.specialites?.some((s) => s.toLowerCase().includes(term))
    );
  });

  const resetPlanningState = () => {
    setActiveTab('infos');
    setModeHoraires('COMMERCE');
    setHoraires([]);
    setCommerceHoraires([]);
    setIndisponibilites([]);
    setPlanningWarning(null);
    setNewPlageByDay({});
    setAbsenceForm(emptyAbsenceForm);
    setEditingAbsenceId(null);
  };

  const openEditModal = (instructeur) => {
    setEditingInstructeur(instructeur);
    setFormData({
      prenom: instructeur.prenom || '',
      nom: instructeur.nom || '',
      email: instructeur.email || '',
      telephone: instructeur.telephone || '',
      specialites: (instructeur.specialites || []).join(', '),
      bio: instructeur.bio || '',
      prestationIds: instructeur.prestationIds || [],
    });
    setPrestationSearch('');
    setFormError(null);
    resetPlanningState();
    setShowModal(true);
    loadPlanning(instructeur.id);
  };

  const openCreateModal = () => {
    setEditingInstructeur(null);
    setFormData(initialFormData);
    setPrestationSearch('');
    setFormError(null);
    resetPlanningState();
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingInstructeur(null);
    setFormData(initialFormData);
    setFormError(null);
    resetPlanningState();
  };

  const applyWarning = (warning) => {
    if (warning?.count) {
      setPlanningWarning(warning);
      toast.warning(formatWarningToast(warning));
    } else {
      setPlanningWarning(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!commercantId) return;
    if (activeTab !== 'infos' && activeTab !== 'prestations') return;

    setFormLoading(true);
    setFormError(null);

    const payload = {
      prenom: formData.prenom.trim(),
      nom: formData.nom.trim(),
      email: formData.email.trim() || null,
      telephone: formData.telephone.trim() || null,
      specialites: formData.specialites,
      bio: formData.bio.trim() || null,
      prestationIds: formData.prestationIds || [],
    };

    try {
      if (editingInstructeur) {
        await apiService.updateInstructeur(editingInstructeur.id, payload);
        toast.success(`${formatInstructeurName(payload)} mis(e) à jour`);
      } else {
        await apiService.createInstructeur(commercantId, payload);
        toast.success(`${formatInstructeurName(payload)} ajouté(e)`);
      }
      closeModal();
      fetchData();
    } catch (err) {
      setFormError(err.message || 'Erreur lors de l’enregistrement');
    } finally {
      setFormLoading(false);
    }
  };

  const handleModeChange = async (nextMode) => {
    if (!editingInstructeur || nextMode === modeHoraires) return;
    try {
      setPlanningLoading(true);
      const res = await apiService.updateInstructeurModeHoraires(
        editingInstructeur.id,
        nextMode,
      );
      setModeHoraires(res.instructeur?.modeHoraires || nextMode);
      applyWarning(res.warning);
      toast.success(
        nextMode === 'COMMERCE'
          ? 'Horaires du commerce utilisés'
          : 'Horaires personnalisés activés',
      );
    } catch (err) {
      toast.error(err.message || 'Changement de mode impossible');
    } finally {
      setPlanningLoading(false);
    }
  };

  const handleAddPlage = async (jourSemaine) => {
    if (!editingInstructeur) return;
    const draft = newPlageByDay[jourSemaine] || { heureDebut: '09:00', heureFin: '12:00' };
    try {
      setPlanningLoading(true);
      const res = await apiService.createInstructeurHoraire(editingInstructeur.id, {
        jourSemaine,
        heureDebut: draft.heureDebut,
        heureFin: draft.heureFin,
      });
      applyWarning(res.warning);
      setNewPlageByDay((prev) => ({ ...prev, [jourSemaine]: { heureDebut: '09:00', heureFin: '12:00' } }));
      await loadPlanning(editingInstructeur.id);
    } catch (err) {
      toast.error(err.message || 'Ajout de plage impossible');
    } finally {
      setPlanningLoading(false);
    }
  };

  const handleDeletePlage = async (horaireId) => {
    if (!editingInstructeur) return;
    try {
      setPlanningLoading(true);
      const res = await apiService.deleteInstructeurHoraire(editingInstructeur.id, horaireId);
      applyWarning(res.warning);
      await loadPlanning(editingInstructeur.id);
    } catch (err) {
      toast.error(err.message || 'Suppression impossible');
    } finally {
      setPlanningLoading(false);
    }
  };

  const handleSaveAbsence = async (e) => {
    e.preventDefault();
    if (!editingInstructeur) return;
    const dateDebut = fromDatetimeLocalValue(absenceForm.dateDebutLocal);
    const dateFin = fromDatetimeLocalValue(absenceForm.dateFinLocal);
    if (!dateDebut || !dateFin) {
      toast.error('Dates d’absence invalides');
      return;
    }
    const payload = {
      dateDebut,
      dateFin,
      motif: absenceForm.motif.trim() || null,
      type: absenceForm.type.trim() || null,
    };
    try {
      setPlanningLoading(true);
      let res;
      if (editingAbsenceId) {
        res = await apiService.updateInstructeurIndisponibilite(
          editingInstructeur.id,
          editingAbsenceId,
          payload,
        );
      } else {
        res = await apiService.createInstructeurIndisponibilite(
          editingInstructeur.id,
          payload,
        );
      }
      applyWarning(res.warning);
      setAbsenceForm(emptyAbsenceForm);
      setEditingAbsenceId(null);
      await loadPlanning(editingInstructeur.id);
      toast.success(editingAbsenceId ? 'Absence mise à jour' : 'Absence ajoutée');
    } catch (err) {
      toast.error(err.message || 'Enregistrement absence impossible');
    } finally {
      setPlanningLoading(false);
    }
  };

  const handleDeleteAbsence = async (indisponibiliteId) => {
    if (!editingInstructeur) return;
    try {
      setPlanningLoading(true);
      await apiService.deleteInstructeurIndisponibilite(
        editingInstructeur.id,
        indisponibiliteId,
      );
      await loadPlanning(editingInstructeur.id);
      toast.success('Absence supprimée');
    } catch (err) {
      toast.error(err.message || 'Suppression impossible');
    } finally {
      setPlanningLoading(false);
    }
  };

  const handleToggleActif = (instructeur) => {
    setConfirmAction({
      title: instructeur.actif ? 'Désactiver ce praticien' : 'Réactiver ce praticien',
      description: instructeur.actif
        ? `${formatInstructeurName(instructeur)} ne pourra plus être assigné(e) aux nouvelles prestations.`
        : `${formatInstructeurName(instructeur)} pourra à nouveau être assigné(e) aux prestations.`,
      confirmLabel: instructeur.actif ? 'Désactiver' : 'Réactiver',
      onConfirm: async () => {
        try {
          if (instructeur.actif) {
            await apiService.deactivateInstructeur(instructeur.id);
            toast.success(`${formatInstructeurName(instructeur)} désactivé(e)`);
          } else {
            await apiService.activateInstructeur(instructeur.id);
            toast.success(`${formatInstructeurName(instructeur)} réactivé(e)`);
          }
          setConfirmAction(null);
          fetchData();
        } catch (err) {
          toast.error(err.message || 'Action impossible');
        }
      },
    });
  };

  const tabs = useMemo(() => {
    const base = [
      { id: 'infos', label: 'Informations' },
      { id: 'prestations', label: 'Prestations' },
    ];
    if (editingInstructeur) {
      base.push({ id: 'horaires', label: 'Horaires' });
      base.push({ id: 'absences', label: 'Absences' });
    }
    return base;
  }, [editingInstructeur]);

  const plagesByDay = useMemo(() => {
    const map = {};
    for (const j of JOURS) map[j.value] = [];
    for (const h of horaires) {
      if (!map[h.jourSemaine]) map[h.jourSemaine] = [];
      map[h.jourSemaine].push(h);
    }
    return map;
  }, [horaires]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {toasts.map((t) => (
        <Toast key={t.id} toast={t} onClose={() => removeToast(t.id)} />
      ))}

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{labels.label}</h1>
          <p className="text-gray-500 mt-1">
            {instructeurs.length} personne{instructeurs.length > 1 ? 's' : ''} — assignables aux prestations et cours
          </p>
        </div>
        <button
          type="button"
          onClick={openCreateModal}
          className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700"
        >
          <Plus className="h-4 w-4" />
          {labels.labelAjouter}
        </button>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {staffWarning && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          {staffWarning}
        </div>
      )}

      <div className="flex flex-col gap-3 md:flex-row md:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Rechercher un praticien..."
            className="w-full rounded-lg border border-gray-200 py-2 pl-10 pr-3 text-sm"
          />
        </div>
        <select
          value={filterStatut}
          onChange={(e) => setFilterStatut(e.target.value)}
          className="rounded-lg border border-gray-200 px-3 py-2 text-sm"
        >
          <option value="">Tous les statuts</option>
          <option value="ACTIF">Actifs</option>
          <option value="INACTIF">Inactifs</option>
        </select>
      </div>

      {filteredInstructeurs.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 bg-white p-10 text-center">
          <Users className="mx-auto h-10 w-10 text-gray-300 mb-3" />
          <p className="text-gray-600">{labels.emptyListHint}</p>
          <button
            type="button"
            onClick={openCreateModal}
            className="mt-4 inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700"
          >
            <Plus className="h-4 w-4" />
            {labels.labelAjouter}
          </button>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Nom</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Contact</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Spécialités</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Prestations</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Statut</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredInstructeurs.map((instructeur) => (
                <tr key={instructeur.id}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{formatInstructeurName(instructeur)}</div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    <div>{instructeur.email || '—'}</div>
                    <div>{instructeur.telephone || ''}</div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {(instructeur.specialites || []).join(', ') || '—'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {instructeur._count?.prestations ?? 0}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${instructeur.actif ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                      {instructeur.actif ? 'Actif' : 'Inactif'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => openEditModal(instructeur)}
                        className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-primary-600"
                        title="Modifier"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleToggleActif(instructeur)}
                        className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-primary-600"
                        title={instructeur.actif ? 'Désactiver' : 'Réactiver'}
                      >
                        {instructeur.actif ? <UserX className="h-4 w-4" /> : <UserCheck className="h-4 w-4" />}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        isOpen={showModal}
        onClose={closeModal}
        title={editingInstructeur ? `Modifier ${labels.labelSingulier.toLowerCase()}` : labels.labelAjouter}
      >
        <div className="mb-4 flex flex-wrap gap-2 border-b border-gray-100 pb-3">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`rounded-lg px-3 py-1.5 text-sm font-medium ${
                activeTab === tab.id
                  ? 'bg-primary-50 text-primary-700'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {planningWarning?.count > 0 && (activeTab === 'horaires' || activeTab === 'absences') && (
          <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            <div className="font-semibold">
              {planningWarning.warningMessage || `${planningWarning.count} rendez-vous sont à replanifier`}
            </div>
            <ul className="mt-2 max-h-28 space-y-1 overflow-y-auto text-xs">
              {(planningWarning.rendezVous || []).slice(0, 20).map((rdv) => (
                <li key={rdv.id}>
                  {rdv.prestationNom || 'RDV'} — {new Date(rdv.dateDebut).toLocaleString('fr-FR')}
                </li>
              ))}
            </ul>
          </div>
        )}

        {(activeTab === 'infos' || activeTab === 'prestations') && (
          <form onSubmit={handleSubmit} className="space-y-4">
            {activeTab === 'infos' && (
              <>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">Prénom *</label>
                    <input
                      type="text"
                      required
                      value={formData.prenom}
                      onChange={(e) => setFormData((prev) => ({ ...prev, prenom: e.target.value }))}
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">Nom *</label>
                    <input
                      type="text"
                      required
                      value={formData.nom}
                      onChange={(e) => setFormData((prev) => ({ ...prev, nom: e.target.value }))}
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">Email</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">Téléphone</label>
                    <input
                      type="tel"
                      value={formData.telephone}
                      onChange={(e) => setFormData((prev) => ({ ...prev, telephone: e.target.value }))}
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Spécialités <span className="font-normal text-gray-400">(secondaire)</span>
                  </label>
                  <input
                    type="text"
                    value={formData.specialites}
                    onChange={(e) => setFormData((prev) => ({ ...prev, specialites: e.target.value }))}
                    placeholder="Texte libre — n’affecte pas l’allocation"
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Bio (optionnel)</label>
                  <textarea
                    value={formData.bio}
                    onChange={(e) => setFormData((prev) => ({ ...prev, bio: e.target.value }))}
                    rows={2}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  />
                </div>
              </>
            )}

            {activeTab === 'prestations' && (
              <div className="rounded-lg border border-gray-200 p-3">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <label className="text-sm font-medium text-gray-700">Prestations réalisées</label>
                  <button
                    type="button"
                    className="text-xs font-semibold text-primary-600 hover:underline"
                    onClick={() => {
                      const visible = prestationsPersonnel
                        .filter((p) => {
                          if (!prestationSearch.trim()) return true;
                          const t = prestationSearch.toLowerCase();
                          return (
                            p.nom?.toLowerCase().includes(t) ||
                            p.categorie?.toLowerCase().includes(t)
                          );
                        })
                        .map((p) => p.id);
                      setFormData((prev) => ({
                        ...prev,
                        prestationIds: [...new Set([...(prev.prestationIds || []), ...visible])],
                      }));
                    }}
                  >
                    Tout sélectionner
                  </button>
                </div>
                <input
                  type="search"
                  value={prestationSearch}
                  onChange={(e) => setPrestationSearch(e.target.value)}
                  placeholder="Rechercher une prestation…"
                  className="mb-2 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                />
                <div className="max-h-48 space-y-2 overflow-y-auto">
                  {(() => {
                    const filtered = prestationsPersonnel.filter((p) => {
                      if (!prestationSearch.trim()) return true;
                      const t = prestationSearch.toLowerCase();
                      return (
                        p.nom?.toLowerCase().includes(t) ||
                        p.categorie?.toLowerCase().includes(t)
                      );
                    });
                    if (filtered.length === 0) {
                      return (
                        <p className="text-xs text-gray-500">
                          Aucune prestation nécessitant du personnel.
                        </p>
                      );
                    }
                    const groups = {};
                    for (const p of filtered) {
                      const key = p.categorie?.trim() || 'Autres';
                      if (!groups[key]) groups[key] = [];
                      groups[key].push(p);
                    }
                    return Object.entries(groups).map(([cat, items]) => (
                      <div key={cat}>
                        <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">
                          {cat}
                        </div>
                        {items.map((p) => {
                          const checked = (formData.prestationIds || []).includes(p.id);
                          return (
                            <label
                              key={p.id}
                              className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-sm hover:bg-gray-50"
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => {
                                  setFormData((prev) => {
                                    const set = new Set(prev.prestationIds || []);
                                    if (set.has(p.id)) set.delete(p.id);
                                    else set.add(p.id);
                                    return { ...prev, prestationIds: [...set] };
                                  });
                                }}
                              />
                              <span>{p.nom}</span>
                            </label>
                          );
                        })}
                      </div>
                    ));
                  })()}
                </div>
              </div>
            )}

            {formError && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {formError}
              </div>
            )}

            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={closeModal} className="rounded-lg border border-gray-200 px-4 py-2 text-sm">
                Annuler
              </button>
              <button
                type="submit"
                disabled={formLoading}
                className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-50"
              >
                {formLoading ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>
          </form>
        )}

        {activeTab === 'horaires' && editingInstructeur && (
          <div className="space-y-4">
            {planningLoading && (
              <p className="text-xs text-gray-500">Chargement du planning…</p>
            )}
            <fieldset className="space-y-2">
              <legend className="mb-1 text-sm font-medium text-gray-700">Mode</legend>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="modeHoraires"
                  checked={modeHoraires === 'COMMERCE'}
                  onChange={() => handleModeChange('COMMERCE')}
                />
                Utiliser les horaires du commerce
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="modeHoraires"
                  checked={modeHoraires === 'PERSONNALISE'}
                  onChange={() => handleModeChange('PERSONNALISE')}
                />
                Personnaliser
              </label>
            </fieldset>

            {modeHoraires === 'COMMERCE' && (
              <div className="rounded-lg border border-gray-100 bg-gray-50 p-3 text-sm text-gray-700">
                <div className="mb-2 font-medium">Horaires du commerce (lecture seule)</div>
                {commerceHoraires.length === 0 ? (
                  <p className="text-xs text-gray-500">Aucun horaire commerce configuré.</p>
                ) : (
                  <ul className="space-y-1 text-xs">
                    {JOURS.map((j) => {
                      const dayRows = commerceHoraires.filter((h) => h.jourSemaine === j.value);
                      return (
                        <li key={j.value}>
                          <span className="font-semibold">{j.label}</span>
                          {': '}
                          {dayRows.length === 0
                            ? 'fermé'
                            : dayRows.map((h) => `${h.heureDebut}–${h.heureFin}`).join(', ')}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}

            {modeHoraires === 'PERSONNALISE' && (
              <div className="max-h-80 space-y-3 overflow-y-auto">
                {JOURS.map((j) => {
                  const dayPlages = plagesByDay[j.value] || [];
                  const draft = newPlageByDay[j.value] || { heureDebut: '09:00', heureFin: '12:00' };
                  return (
                    <div key={j.value} className="rounded-lg border border-gray-200 p-3">
                      <div className="mb-2 flex items-center justify-between">
                        <div className="text-sm font-semibold text-gray-800">{j.label}</div>
                        {dayPlages.length === 0 && (
                          <span className="text-xs font-medium text-gray-500">OFF</span>
                        )}
                      </div>
                      {dayPlages.length === 0 ? (
                        <p className="mb-2 text-xs text-gray-500">Aucune plage — jour off</p>
                      ) : (
                        <ul className="mb-2 space-y-1">
                          {dayPlages.map((h) => (
                            <li key={h.id} className="flex items-center justify-between text-sm">
                              <span>
                                {h.heureDebut}–{h.heureFin}
                              </span>
                              <button
                                type="button"
                                className="text-xs text-red-600 hover:underline"
                                onClick={() => handleDeletePlage(h.id)}
                              >
                                Supprimer
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                      <div className="flex flex-wrap items-end gap-2">
                        <div>
                          <label className="mb-0.5 block text-xs text-gray-500">Début</label>
                          <input
                            type="time"
                            value={draft.heureDebut}
                            onChange={(e) =>
                              setNewPlageByDay((prev) => ({
                                ...prev,
                                [j.value]: { ...draft, heureDebut: e.target.value },
                              }))
                            }
                            className="rounded border border-gray-200 px-2 py-1 text-sm"
                          />
                        </div>
                        <div>
                          <label className="mb-0.5 block text-xs text-gray-500">Fin</label>
                          <input
                            type="time"
                            value={draft.heureFin}
                            onChange={(e) =>
                              setNewPlageByDay((prev) => ({
                                ...prev,
                                [j.value]: { ...draft, heureFin: e.target.value },
                              }))
                            }
                            className="rounded border border-gray-200 px-2 py-1 text-sm"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => handleAddPlage(j.value)}
                          className="rounded-lg bg-primary-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-primary-700"
                        >
                          Ajouter une plage
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="flex justify-end pt-2">
              <button type="button" onClick={closeModal} className="rounded-lg border border-gray-200 px-4 py-2 text-sm">
                Fermer
              </button>
            </div>
          </div>
        )}

        {activeTab === 'absences' && editingInstructeur && (
          <div className="space-y-4">
            <form onSubmit={handleSaveAbsence} className="space-y-3 rounded-lg border border-gray-200 p-3">
              <div className="text-sm font-medium text-gray-700">
                {editingAbsenceId ? 'Modifier l’absence' : 'Ajouter une absence'}
              </div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div>
                  <label className="mb-1 block text-xs text-gray-500">Début</label>
                  <input
                    type="datetime-local"
                    required
                    value={absenceForm.dateDebutLocal}
                    onChange={(e) =>
                      setAbsenceForm((prev) => ({ ...prev, dateDebutLocal: e.target.value }))
                    }
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs text-gray-500">Fin</label>
                  <input
                    type="datetime-local"
                    required
                    value={absenceForm.dateFinLocal}
                    onChange={(e) =>
                      setAbsenceForm((prev) => ({ ...prev, dateFinLocal: e.target.value }))
                    }
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div>
                  <label className="mb-1 block text-xs text-gray-500">Motif (optionnel)</label>
                  <input
                    type="text"
                    value={absenceForm.motif}
                    onChange={(e) =>
                      setAbsenceForm((prev) => ({ ...prev, motif: e.target.value }))
                    }
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs text-gray-500">Type (optionnel)</label>
                  <input
                    type="text"
                    value={absenceForm.type}
                    onChange={(e) =>
                      setAbsenceForm((prev) => ({ ...prev, type: e.target.value }))
                    }
                    placeholder="CONGE, MALADIE…"
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={planningLoading}
                  className="rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-50"
                >
                  {editingAbsenceId ? 'Mettre à jour' : 'Ajouter'}
                </button>
                {editingAbsenceId && (
                  <button
                    type="button"
                    className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm"
                    onClick={() => {
                      setEditingAbsenceId(null);
                      setAbsenceForm(emptyAbsenceForm);
                    }}
                  >
                    Annuler
                  </button>
                )}
              </div>
            </form>

            <div className="max-h-56 space-y-2 overflow-y-auto">
              {indisponibilites.length === 0 ? (
                <p className="text-sm text-gray-500">Aucune absence active.</p>
              ) : (
                indisponibilites.map((ind) => (
                  <div
                    key={ind.id}
                    className="flex flex-wrap items-start justify-between gap-2 rounded-lg border border-gray-100 px-3 py-2 text-sm"
                  >
                    <div>
                      <div className="font-medium text-gray-800">
                        {new Date(ind.dateDebut).toLocaleString('fr-FR')}
                        {' → '}
                        {new Date(ind.dateFin).toLocaleString('fr-FR')}
                      </div>
                      <div className="text-xs text-gray-500">
                        {[ind.type, ind.motif].filter(Boolean).join(' — ') || 'Sans motif'}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        className="text-xs text-primary-600 hover:underline"
                        onClick={() => {
                          setEditingAbsenceId(ind.id);
                          setAbsenceForm({
                            dateDebutLocal: toDatetimeLocalValue(ind.dateDebut),
                            dateFinLocal: toDatetimeLocalValue(ind.dateFin),
                            motif: ind.motif || '',
                            type: ind.type || '',
                          });
                        }}
                      >
                        Modifier
                      </button>
                      <button
                        type="button"
                        className="text-xs text-red-600 hover:underline"
                        onClick={() => handleDeleteAbsence(ind.id)}
                      >
                        Supprimer
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="flex justify-end pt-2">
              <button type="button" onClick={closeModal} className="rounded-lg border border-gray-200 px-4 py-2 text-sm">
                Fermer
              </button>
            </div>
          </div>
        )}
      </Modal>

      {confirmAction && (
        <ConfirmDialog
          isOpen
          title={confirmAction.title}
          description={confirmAction.description}
          confirmLabel={confirmAction.confirmLabel}
          onConfirm={confirmAction.onConfirm}
          onCancel={() => setConfirmAction(null)}
        />
      )}
    </div>
  );
};

export default Praticiens;
